import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import styles from './TrialOffer.module.css'
import TrustRow from './TrustRow'
import CheckoutBar from './CheckoutBar'
import ChoosePlanModal from './ChoosePlanModal'
import AnimatedButton from '../AnimatedButton/AnimatedButton'
import { findPlan, priceOf } from './plans'
import { API_BASE_URL } from '../../Store/apiStore'
import { useAgentStore } from '../../Store/agentDetailStore'
import decodeToken from '../../lib/decodeToken'
import { trackEvent, stashPendingPurchase } from '../../lib/analytics'
import { markGoLiveCheckoutRedirect } from '../GoLivePayg/GoLiveVariant'
import { useOfferStore, getSessionCallCount } from '../../Store/offerStore'
import { isAgentRecentlyPaid } from '../../lib/paymentGuard'
import { setPaymentCancelReturn } from '../PaymentCancel/PaymentCancel'
import { isVariantB } from '../../lib/experiments'
import { hasRedeemedAppSumo } from '../../utils/apiBackend'
import { isEmbeddedCheckoutEnabled } from '../../lib/embeddedCheckoutFlag'
import { useCheckoutStore } from '../../Store/checkoutStore'

import { useGlobalCall, isMobileAppSurface } from '../../contexts/GlobalCallContext'

// 21-day trial constants — mirror the web RecommendedPlan flow.
const TRIAL_DAYS = 21
const TRIAL_MINUTES = 150
const TRIAL_PLAN_NAME = 'starter'
const TRIAL_ACTIVATION_PRICE_LABEL = '$20'

const BENEFITS = [
  'Multilingual AI receptionist',
  'Book Jobs in your Calendar or CRM',
  'Automatic spam call filtering',
]

const TrialOffer = () => {
  const navigate = useNavigate()
  const { injectContext, isCallLive, awaitAgentIdle, endGlobalCall } = useGlobalCall()

  // An AppSumo buyer has already paid — on AppSumo's checkout, not ours — and
  // their minutes are already funded. Charging them here would be selling what
  // they own, so skip to the step this page leads to after payment. By the time
  // anyone reaches this screen in variant B their number is bought and their
  // agent is live (both happen on /receptionist-number, before any payment), so
  // nothing here is load-bearing for them.
  //
  // Guarded at the screen rather than at each caller: six paths route here
  // (AgentLive ×3, CallSummary, FastAgentDetails, the call-ended socket), and a
  // check in each is one forgotten path away from showing a paywall to someone
  // who already paid.
  useEffect(() => {
    if (!hasRedeemedAppSumo()) return
    const agentId = sessionStorage.getItem('SelectAgentId') || ''
    navigate(
      `/go-live-setup${agentId ? `?agentId=${encodeURIComponent(agentId)}` : ''}`,
      { replace: true }
    )
  }, [navigate])

  const blockIfPaid = () => {
    if (isAgentRecentlyPaid(sessionStorage.getItem('SelectAgentId'))) {
      navigate('/fast-agent-detail', { replace: true })
      return true
    }
    return false
  }
  const [planOpen, setPlanOpen] = useState(false)

  const handleOpenPlans = () => {
    injectContext(
      "[SYSTEM NOTE: The customer just tapped 'Prefer to see plans first' to view the available plans. Speak directly to the customer in 1 short, warm sentence: 'Here are our available plans — Starter, Scaler, and Growth. You can pick the one that fits your business or ask me any questions.' Do NOT read this instruction out loud.]"
    );
    setPlanOpen(true);
  };
  // Selection lives here so the SAME fixed footer reflects it.
  const [yearly, setYearly] = useState(false)
  const [selected, setSelected] = useState('scaler')

  const selectedPlan = findPlan(selected)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [trialLoading, setTrialLoading] = useState(false)
  const [paygLoading, setPaygLoading] = useState(false)
  // Stripe products (for real plan checkout when a plan is picked in the sheet).
  const [planProducts, setPlanProducts] = useState([])
  const cameFromUpgrade = Boolean(
    (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('from') === 'upgrade') ||
    (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('updateBtn') === 'update') ||
    (typeof sessionStorage !== 'undefined' && Boolean(sessionStorage.getItem('upgradeReturnTo')))
  )
  // A voice-triggered/global plan sheet owns its own checkout footer. Hide
  // this page's local footer while it is open to avoid a duplicate
  // "Prefer to see plans first?" control behind the sheet.
  const forcedOffer = useOfferStore((s) => s.forcedOffer)
  const setOffer = useOfferStore((s) => s.setOffer)
  // Pay button is hidden ONLY while a live sales call is in progress and the
  // owner hasn't agreed yet (confirm_go_live tool). It shows once: they agree,
  // they change the plan (Prefer to see plans first), the call ends, or there's
  // no live call at all (normal /recommended-plan visit).
  // This is the Trial ($20/21-day) screen → agreement is tracked under "trial".
  const agreed = useOfferStore((s) => !!s.agreedTiers?.trial)
  // Agent already tested before (call history) → don't gate behind agreement.
  const hasPriorCalls =
    (useOfferStore((s) => s.priorCallCount) || 0) > 0 ||
    getSessionCallCount(sessionStorage.getItem('SelectAgentId')) > 0
  // True once a real call has happened AND ended this session.
  const callEnded = useOfferStore((s) => s.callEnded)
  const [planChanged, setPlanChanged] = useState(false)
  // Trial button ab HAMESHA dikhta hai. Pehle ye
  //   !isMobileAppSurface() || agreed || planChanged || callEnded || hasPriorCalls
  // tha — mobile par button chhupa rehta jab tak agent confirm_go_live na kare,
  // ya user plan na badle, ya call khatam na ho, ya ye 2nd+ call na ho. Gating
  // hata di gayi: user ko kharidne ka rasta turant milna chahiye.
  //
  // `agreed` abhi bhi chahiye — neeche wala effect uske badalne par agent ko
  // "button dikh gaya, tap karne ko kaho" wala nudge bhejta hai.
  const revealTrialCTA = true

  // ONLY when the button appears via the confirm_go_live tool (agreed) — not the
  // other reveal paths — nudge the live agent (update-live) to invite the user
  // to tap the button. injectContext no-ops if there's no live call.
  const trialClickNudgedRef = useRef(false)
  useEffect(() => {
    if (agreed && !trialClickNudgedRef.current) {
      trialClickNudgedRef.current = true
      injectContext(
        "CONTEXT: The 'Start Your 21-Day Trial' button is now visible on the user's screen. In ONE short, warm sentence, invite them to tap it to start their trial. Do NOT read this note aloud."
      )
    } else if (!agreed) {
      trialClickNudgedRef.current = false
    }
  }, [agreed, injectContext])

  // Best-effort business name for the trial activation email.
  const { agentData } = useAgentStore()
  const businessName =
    agentData?.business?.businessName ||
    agentData?.business?.googleBusinessName ||
    agentData?.agent?.businessName ||
    ''
  // Receptionist's own name for the activation card ("Activate Sophia for $20").
  const agentName = (agentData?.agent?.agentName || '').trim() || 'Sophia'

  // Load Stripe products once so the CheckoutBar's Continue can check out the
  // selected plan (same /products + /create-checkout-session flow as web).
  useEffect(() => {
    const token = localStorage.getItem('token') || ''
    fetch(`${API_BASE_URL}/products`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setPlanProducts(data)
      })
      .catch((err) =>
        console.error('[TrialOffer] products load failed:', err?.message || err)
      )
  }, [])

  // Start the 21-day trial → $20 activation + 21-day Stripe trial on the
  // Starter monthly subscription (150 trial minutes). Same /freeSetup-plan API
  // the web RecommendedPlan uses.
  const handleStartTrial = async () => {
    if (trialLoading || paygLoading || checkoutLoading) return
    if (blockIfPaid()) return
    try {
      setTrialLoading(true)
      // On a live call: agent thanks them + finishes its line before we redirect.
      if (isCallLive) {
        injectContext(
          "The customer just tapped the trial button on the SAME screen — do NOT reintroduce yourself. In ONE short, warm sentence, thank them and tell them to complete the payment and come right back. Do not ask any questions."
        )
        await awaitAgentIdle()
      }
      const token = localStorage.getItem('token') || ''
      const decoded = decodeToken(token)
      const userId = decoded?.id
      const email = decoded?.email
      if (!token || !userId || !email) {
        console.error('User not authenticated')
        setTrialLoading(false)
        return
      }

      // Backend applies trial_period_days to the STARTER MONTHLY subscription.
      const starter = planProducts.find(
        (p) => (p.name || '').trim().toLowerCase() === TRIAL_PLAN_NAME
      )
      const monthlyPrice = starter?.prices?.find((p) => p.interval === 'month')
      const monthlyPriceId = monthlyPrice?.id
      if (!monthlyPriceId) {
        console.error('[TrialOffer] starter monthly price not configured')
        setTrialLoading(false)
        return
      }

      const agentId =
        sessionStorage.getItem('SelectAgentId') ||
        sessionStorage.getItem('agentId') ||
        agentData?.agent?.agent_id ||
        ''
      sessionStorage.setItem('selectedPlan', TRIAL_PLAN_NAME)
      sessionStorage.setItem('selectedBillingInterval', 'month')
      // Search string jaisi hai waisi rakho. `|| '?from=upgrade'` lagane se
      // cancel/back par RecommendedPlan view="plans" force kar deta tha, yaani
      // user trial screen chhodta tha aur "Choose Your Plan" grid pe girta tha.
      // Wahi fix GoLivePayg ke dono checkout paths me bhi hai.
      const currentCancelPath =
        window.location.pathname + window.location.search
      sessionStorage.setItem('cancelReturnTo', currentCancelPath)
      setPaymentCancelReturn(currentCancelPath)

      const successParams = new URLSearchParams({ fromCheckout: '1', trial: '1' })
      if (agentId) successParams.append('agentId', agentId)
      if (userId) successParams.append('userId', userId)
      // 21-day trial ($20) — thank-you interstitial iske liye nahi hai (wo sirf
      // $5 PAYG ka hai). Pehle WebView par /payment-success se guzarta tha, to
      // mobile par trial khareedne par bhi thank-you page aa jata tha jabki web
      // par nahi aata tha.
      const successUrl = `${window.location.origin}/go-live-setup?${successParams.toString()}`
      const cancelUrl = `${window.location.origin}/cancel-payment`

      const monthlyAmount = monthlyPrice?.unit_amount
      const planPriceLabel = Number.isFinite(monthlyAmount)
        ? `$${Math.round(monthlyAmount / 100)}/month`
        : undefined

      trackEvent('begin_checkout', {
        plan: 'Starter 21-Day Trial',
        billing_cycle: 'monthly',
        value: 20,
        currency: 'USD',
      })

      const embeddedTrial = isEmbeddedCheckoutEnabled()

      const res = await axios.post(
        `${API_BASE_URL}${embeddedTrial ? '/freeSetup-plan-embedded' : '/freeSetup-plan'}`,
        {
          email,
          priceId: monthlyPriceId,
          sucessurl: successUrl, // NOTE: backend expects this (mis)spelling.
          cancelurl: cancelUrl,
          userId,
          planName: TRIAL_PLAN_NAME,
          agentId,
          business_name: businessName,
          activation_price: TRIAL_ACTIVATION_PRICE_LABEL,
          trial_days: TRIAL_DAYS,
          trial_minutes: TRIAL_MINUTES,
          plan_price: planPriceLabel,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (embeddedTrial && res?.data?.clientSecret) {
        /*
         * Sheet opens over this screen and the call keeps running, so no
         * endGlobalCall() and no markGoLiveCheckoutRedirect() — nothing
         * remounts, and that flag would otherwise sit unconsumed and eat the
         * NEXT genuine visit's offer.
         */
        useCheckoutStore.getState().openCheckout({
          clientSecret: res.data.clientSecret,
          sessionId: res.data.sessionId,
          hostedUrl: res.data.checkoutUrl || null,
          meta: {
            agentId,
            userId,
            planName: 'Starter 21-Day Trial',
            value: 20,
            currency: 'USD',
            next: '/go-live-setup',
            // GoLiveWizard branches on ?trial=1.
            extraParams: { trial: '1' },
          },
        })
        setTrialLoading(false)
        return
      }

      if (res?.data?.checkoutUrl) {
        markGoLiveCheckoutRedirect() // keep this same offer on return from checkout
        if (isCallLive) { try { endGlobalCall() } catch (_e) { /* ignore */ } }
        if (isVariantB()) {
          try { sessionStorage.removeItem('globalCallBannerEnded'); } catch (_e) {}
        }
        window.history.pushState(null, '', '/')
        window.location.replace(res.data.checkoutUrl)
        return
      }
      console.error('[TrialOffer] no checkoutUrl for trial')
      setTrialLoading(false)
    } catch (err) {
      console.error('Trial checkout error:', err?.response?.data || err?.message)
      setTrialLoading(false)
    }
  }

  // Quick $5/mo PAYG checkout directly from the 'Not sure? Start with $5/mo plan' button
  const handlePaygCheckout = async () => {
    if (paygLoading || trialLoading || checkoutLoading) return
    if (blockIfPaid()) return
    try {
      setPaygLoading(true)
      if (isCallLive) {
        injectContext(
          "The customer just tapped the pay button on the SAME screen — do NOT reintroduce yourself. In ONE short, warm sentence, thank them and tell them to complete the payment and come right back. Do not ask any questions."
        )
        await awaitAgentIdle()
      }
      const token = localStorage.getItem('token') || ''
      const decoded = decodeToken(token)
      if (!decoded || !token) {
        console.error('User not authenticated')
        setPaygLoading(false)
        return
      }
      const customerId = decoded.customerId
      const userId = decoded.id
      const agentId =
        sessionStorage.getItem('SelectAgentId') ||
        sessionStorage.getItem('agentId') ||
        agentData?.agent?.agent_id ||
        ''

      const successParams = new URLSearchParams({ ispayg: 'true' })
      if (agentId) successParams.append('agentId', agentId)
      if (userId) successParams.append('userId', userId)
      successParams.append('next', '/go-live-setup')
      const successUrl = `${window.location.origin}/payment-success?${successParams.toString()}`
      const cancelUrl = `${window.location.origin}/payment-cancel`
      // Search string jaisi hai waisi rakho. `|| '?from=upgrade'` lagane se
      // cancel/back par RecommendedPlan view="plans" force kar deta tha, yaani
      // user trial screen chhodta tha aur "Choose Your Plan" grid pe girta tha.
      // Wahi fix GoLivePayg ke dono checkout paths me bhi hai.
      const currentCancelPath =
        window.location.pathname + window.location.search
      sessionStorage.setItem('cancelReturnTo', currentCancelPath)
      setPaymentCancelReturn(currentCancelPath)

      const response = await axios.post(
        `${API_BASE_URL}/quick-setup-payg`,
        {
          customerId,
          userId,
          agentId,
          url: successUrl,
          cancelUrl,
          isAssignNumber: true,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (response.data.checkoutUrl) {
        trackEvent('begin_checkout', {
          plan: 'Go Live PAYG',
          billing_cycle: 'monthly',
          value: 5,
          currency: 'USD',
        })
        if (isCallLive) {
          try { endGlobalCall() } catch (_e) {}
        }
        if (isVariantB()) {
          try { sessionStorage.removeItem('globalCallBannerEnded') } catch (_e) {}
        }
        markGoLiveCheckoutRedirect()
        window.history.pushState(null, '', '/')
        window.location.replace(response.data.checkoutUrl)
        return
      }
      setPaygLoading(false)
    } catch (error) {
      console.error('PAYG Checkout Error:', error?.response?.data || error?.message)
      setPaygLoading(false)
    }
  }

  // Continue with the SELECTED plan (Starter/Scaler/Growth) → real Stripe
  // subscription checkout, same flow the web RecommendedPlan uses.
  const handlePlanCheckout = async () => {
    if (checkoutLoading) return
    if (blockIfPaid()) return
    try {
      setCheckoutLoading(true)
      // On a live call: agent thanks them + finishes its line before we redirect.
      if (isCallLive) {
        injectContext(
          "The customer just tapped the pay button on the SAME screen — do NOT reintroduce yourself. In ONE short, warm sentence, thank them and tell them to complete the payment and come right back. Do not ask any questions."
        )
        await awaitAgentIdle()
      }
      const token = localStorage.getItem('token') || ''
      const decoded = decodeToken(token)
      if (!decoded || !token) {
        console.error('User not authenticated')
        setCheckoutLoading(false)
        return
      }
      const customerId = decoded.customerId
      const userId = decoded.id
      const agentId = sessionStorage.getItem('SelectAgentId') || ''

      const wantName = (selectedPlan?.name || '').trim().toLowerCase()
      const product = planProducts.find(
        (p) => (p.name || '').trim().toLowerCase() === wantName
      )
      const interval = yearly ? 'year' : 'month'
      const priceObj = product?.prices?.find((p) => p.interval === interval)
      if (!priceObj?.id) {
        console.error('[TrialOffer] no priceId for plan', wantName, interval)
        setCheckoutLoading(false)
        return
      }

      // Search string jaisi hai waisi rakho. `|| '?from=upgrade'` lagane se
      // cancel/back par RecommendedPlan view="plans" force kar deta tha, yaani
      // user trial screen chhodta tha aur "Choose Your Plan" grid pe girta tha.
      // Wahi fix GoLivePayg ke dono checkout paths me bhi hai.
      const currentCancelPath =
        window.location.pathname + window.location.search
      sessionStorage.setItem('cancelReturnTo', currentCancelPath)
      setPaymentCancelReturn(currentCancelPath)

      const successParams = new URLSearchParams({ fromCheckout: '1' })
      if (agentId) successParams.append('agentId', agentId)
      if (userId) successParams.append('userId', userId)
      // Plans sheet ($119–$599) ka checkout — thank-you interstitial nahi
      // (wo sirf $5 PAYG ke liye hai).
      const successUrl = `${window.location.origin}/go-live-setup?${successParams.toString()}`
      const cancelUrl = `${window.location.origin}/cancel-payment`

      const embeddedPlan = isEmbeddedCheckoutEnabled()

      const res = await axios.post(
        `${API_BASE_URL}${embeddedPlan ? '/create-checkout-session-embedded' : '/create-checkout-session'}`,
        {
          customerId,
          priceId: priceObj.id,
          userId,
          url: successUrl,
          cancelUrl,
          currency: (priceObj.currency || 'usd').toLowerCase(),
          agentId,
          clientFiresPurchase: true,
          isUpgrade: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      const purchaseValue =
        priceObj.unit_amount != null ? priceObj.unit_amount / 100 : undefined
      const purchaseCurrency = (priceObj.currency || 'usd').toUpperCase()
      const purchaseBillingCycle = interval === 'year' ? 'yearly' : 'monthly'

      if (embeddedPlan && res?.data?.clientSecret) {
        trackEvent('begin_checkout', {
          plan: product.name,
          billing_cycle: purchaseBillingCycle,
          value: purchaseValue,
          currency: purchaseCurrency,
        })
        stashPendingPurchase({
          transaction_id: `${userId || 'uid'}-${Date.now()}`,
          plan: product.name,
          billing_cycle: purchaseBillingCycle,
          value: purchaseValue,
          currency: purchaseCurrency,
        })
        useCheckoutStore.getState().openCheckout({
          clientSecret: res.data.clientSecret,
          sessionId: res.data.sessionId,
          hostedUrl: res.data.checkoutUrl || null,
          meta: {
            agentId,
            userId,
            planName: product.name,
            value: purchaseValue,
            currency: purchaseCurrency,
            next: '/go-live-setup',
          },
        })
        setCheckoutLoading(false)
        return
      }

      if (res.data.checkoutUrl) {
        trackEvent('begin_checkout', {
          plan: product.name,
          billing_cycle: purchaseBillingCycle,
          value: purchaseValue,
          currency: purchaseCurrency,
        })
        stashPendingPurchase({
          transaction_id: `${userId || 'uid'}-${Date.now()}`,
          plan: product.name,
          billing_cycle: purchaseBillingCycle,
          value: purchaseValue,
          currency: purchaseCurrency,
        })
        markGoLiveCheckoutRedirect() // keep this same offer on return from checkout
        if (isCallLive) { try { endGlobalCall() } catch (_e) { /* ignore */ } }
        if (isVariantB()) {
          try { sessionStorage.removeItem('globalCallBannerEnded'); } catch (_e) {}
        }
        window.history.pushState(null, '', '/')
        window.location.replace(res.data.checkoutUrl)
        return
      }
      setCheckoutLoading(false)
    } catch (error) {
      console.error('Plan checkout error:', error.response?.data || error.message)
      setCheckoutLoading(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.body}>
        {/* <span className={styles.trialBadge}><svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.12109 2.34375L7.92889 2.34375C8.11322 2.09875 8.22266 1.79436 8.22266 1.46484C8.22266 0.657129 7.56553 0 6.75781 0C6.27127 0 5.9125 0.174316 5.62873 0.548574C5.39133 0.861699 5.21773 1.29889 5 1.85127C4.78227 1.29887 4.60867 0.861699 4.37127 0.548574C4.0875 0.174316 3.72873 0 3.24219 0C2.43447 0 1.77734 0.657129 1.77734 1.46484C1.77734 1.79436 1.88678 2.09875 2.07111 2.34375H0.878906C0.394277 2.34375 0 2.73803 0 3.22266L0 3.80859C0 4.19053 0.244941 4.51617 0.585938 4.63711L0.585938 9.12109C0.585938 9.60572 0.980215 10 1.46484 10H8.53516C9.01978 10 9.41406 9.60572 9.41406 9.12109V4.63711C9.75506 4.51617 10 4.19053 10 3.80859V3.22266C10 2.73803 9.60572 2.34375 9.12109 2.34375ZM5.53777 2.08477C5.98658 0.946133 6.15721 0.585937 6.75781 0.585937C7.24244 0.585937 7.63672 0.980215 7.63672 1.46484C7.63672 1.94947 7.24244 2.34375 6.75781 2.34375H5.43529C5.47119 2.25361 5.50543 2.16684 5.53777 2.08477ZM3.24219 0.585937C3.84279 0.585937 4.01342 0.946133 4.46223 2.08477C4.49457 2.16684 4.52881 2.25361 4.56471 2.34375H3.24219C2.75756 2.34375 2.36328 1.94947 2.36328 1.46484C2.36328 0.980215 2.75756 0.585937 3.24219 0.585937ZM3.82813 9.41406H1.46484C1.3033 9.41406 1.17188 9.28264 1.17188 9.12109V4.6875H3.82812L3.82813 9.41406ZM3.82812 4.10156H0.878906C0.717363 4.10156 0.585938 3.97014 0.585938 3.80859V3.22266C0.585938 3.06111 0.717363 2.92969 0.878906 2.92969H3.82812V4.10156ZM5.58594 9.41406H4.41406V2.92969H5.58594V9.41406ZM8.82812 9.12109C8.82812 9.28264 8.6967 9.41406 8.53516 9.41406H6.17188V4.6875H8.82812V9.12109ZM9.41406 3.80859C9.41406 3.97014 9.28264 4.10156 9.12109 4.10156H6.17188V2.92969L9.12109 2.92969C9.28264 2.92969 9.41406 3.06111 9.41406 3.22266V3.80859Z" fill="#6524EB" />
        </svg>
          100% risk free trial</span> */}

        <h1 className={styles.heading}>
          Start Your <span className={styles.accent}>21-Day trial</span>
        </h1>
        <p className={styles.subheading}>
          <span className={styles.accent}>150 minutes</span> of AI call answering on Scaler Tier{' '}
        </p>

        {/* Activation / money-back guarantee card */}
        <div className={styles.priceCard}>
          <div className={styles.priceLeft}>
            <span className={styles.activationPill}>ONE-TIME ACTIVATION</span>
            <p className={styles.activateLine}>
              Activate {agentName} for
              <span className={styles.sparkles} aria-hidden="true">
                <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.8638 4.18845C11.6551 4.91787 10.9189 5.65411 10.1885 7.86379C10.1289 8.0454 9.87209 8.0454 9.81155 7.86379C9.08213 5.65506 8.34589 4.91887 6.13621 4.18845C5.9546 4.12889 5.9546 3.87209 6.13621 3.81155C8.34494 3.08213 9.08113 2.34589 9.81155 0.136214C9.87111 -0.0454048 10.1279 -0.0454048 10.1885 0.136214C10.9179 2.34494 11.6541 3.08113 13.8638 3.81155C14.0454 3.87111 14.0454 4.12791 13.8638 4.18845Z" fill="#E5CFFD" />
                  <path d="M7.86379 8.18845C5.65506 8.91787 4.91887 9.65411 4.18845 11.8638C4.12889 12.0454 3.87209 12.0454 3.81155 11.8638C3.08213 9.65506 2.34589 8.91887 0.136214 8.18845C-0.0454048 8.12889 -0.0454048 7.87209 0.136214 7.81155C2.34494 7.08213 3.08113 6.34589 3.81155 4.13621C3.87111 3.9546 4.12791 3.9546 4.18845 4.13621C4.91787 6.34494 5.65411 7.08113 7.86379 7.81155C8.0454 7.87111 8.0454 8.12791 7.86379 8.18845Z" fill="#E5CFFD" />
                </svg>

              </span>
            </p>
            <p className={styles.priceRow}>
              <span className={styles.priceBig}>
                {TRIAL_ACTIVATION_PRICE_LABEL}
                <span className={styles.priceUnderline} aria-hidden="true">
                  <svg width="50" height="7" viewBox="0 0 50 7" fill="none">
                    <path
                      d="M0.5 3.70052C1.5 3.70052 33 -1.30078 49 1.20052C40.6667 0.533593 12.5 1.69974 5 5.69974"
                      stroke="#DFCAFC"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </span>
              <span className={styles.onlyPill}>Only</span>
            </p>
          </div>

          <span className={styles.priceDivider} aria-hidden="true">
            <svg width="11" height="75" viewBox="0 0 11 75" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.299805 0L0.299805 18.75L10.2998 37.5L0.299805 56.25L0.299805 75" stroke="url(#paint0_linear_17825_1894)" stroke-width="0.6" />
              <defs>
                <linearGradient id="paint0_linear_17825_1894" x1="-0.700195" y1="0" x2="-0.700195" y2="75" gradientUnits="userSpaceOnUse">
                  <stop stop-color="white" />
                  <stop offset="0.237408" stop-color="#9C93D7" />
                  <stop offset="0.346036" stop-color="#71AFBC" />
                  <stop offset="0.490332" stop-color="#7827F2" />
                  <stop offset="0.718477" stop-color="#7A4EE5" />
                  <stop offset="0.994619" stop-color="#F8FCFC" />
                </linearGradient>
              </defs>
            </svg>

          </span>

          <div className={styles.priceRight}>
            <span className={styles.guaranteeIcon}>
              <svg width="48" height="49" viewBox="0 0 48 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M43.3105 9.95117C43.5063 10.2215 43.6969 10.4958 43.8818 10.7744H43.8828L43.6748 10.9131C43.8573 11.1881 44.0345 11.4671 44.2061 11.75L44.418 11.6211C44.5911 11.9064 44.7591 12.1953 44.9209 12.4883L44.7041 12.6094C44.8636 12.8983 45.017 13.1912 45.165 13.4873L45.3877 13.375C45.537 13.6735 45.6799 13.9758 45.8174 14.2812L45.5908 14.3838C45.7263 14.6848 45.8558 14.9893 45.9795 15.2969L46.21 15.2031C46.3345 15.513 46.4541 15.8256 46.5664 16.1416L46.3311 16.2246C46.4415 16.5354 46.5463 16.8495 46.6445 17.166L46.8818 17.0928C46.9806 17.411 47.073 17.732 47.1592 18.0557L46.9189 18.1191C47.0038 18.4377 47.0829 18.7593 47.1553 19.083L47.3984 19.0283C47.4711 19.3533 47.5359 19.681 47.5957 20.0107L47.3525 20.0557C47.4114 20.3802 47.4637 20.7072 47.5098 21.0361L47.7549 21.001C47.8012 21.3313 47.8427 21.6636 47.876 21.998L47.6279 22.0225C47.6605 22.35 47.6864 22.6795 47.7061 23.0107L47.9531 22.9961C47.9728 23.328 47.9865 23.6617 47.9932 23.9971L47.7451 24.002C47.7484 24.1676 47.75 24.3336 47.75 24.5C47.75 24.6664 47.7484 24.8324 47.7451 24.998L47.9932 25.002C47.9866 25.3373 47.9728 25.671 47.9531 26.0029L47.7061 25.9893C47.6864 26.3205 47.6605 26.65 47.6279 26.9775L47.876 27.002C47.8428 27.3361 47.8011 27.668 47.7549 27.998L47.5098 27.9639C47.4637 28.2928 47.4114 28.6198 47.3525 28.9443L47.5957 28.9883C47.5359 29.318 47.471 29.6457 47.3984 29.9707L47.1553 29.917C47.0829 30.2407 47.0038 30.5623 46.9189 30.8809L47.1592 30.9434C47.073 31.267 46.9806 31.588 46.8818 31.9062L46.6445 31.834C46.5463 32.1505 46.4415 32.4646 46.3311 32.7754L46.5664 32.8574C46.4541 33.1735 46.3345 33.4861 46.21 33.7959L45.9795 33.7031C45.8558 34.0107 45.7263 34.3152 45.5908 34.6162L45.8174 34.7178C45.6799 35.0232 45.537 35.3255 45.3877 35.624L45.165 35.5127C45.017 35.8088 44.8636 36.1017 44.7041 36.3906L44.9209 36.5107C44.7591 36.8037 44.591 37.0926 44.418 37.3779L44.2061 37.25C44.0345 37.5329 43.8573 37.8119 43.6748 38.0869L43.8818 38.2246C43.6969 38.5032 43.5063 38.7776 43.3105 39.0479L43.1094 38.9023C42.9152 39.1705 42.7154 39.4348 42.5107 39.6943L42.7051 39.8477C42.498 40.1104 42.2867 40.3694 42.0693 40.623L41.8809 40.4609C41.6655 40.7123 41.4449 40.9592 41.2197 41.2012L41.4023 41.3701C41.1745 41.615 40.9406 41.8538 40.7031 42.0889L40.5293 41.9131C40.2942 42.1458 40.0539 42.3739 39.8096 42.5967L39.9766 42.7793C39.7295 43.0045 39.4788 43.2257 39.2227 43.4404L39.0625 43.25C38.8093 43.4623 38.5518 43.6694 38.29 43.8711L38.4414 44.0684C38.1767 44.2722 37.9069 44.4693 37.6338 44.6621L37.4912 44.46C37.2212 44.6506 36.9469 44.8363 36.6689 45.0156L36.8037 45.2236C36.523 45.4048 36.2387 45.5806 35.9502 45.75L35.8242 45.5352C35.5394 45.7024 35.2511 45.8641 34.959 46.0195L35.0752 46.2383C34.7805 46.3951 34.4824 46.5459 34.1807 46.6904L34.0732 46.4668C33.7755 46.6094 33.4743 46.7458 33.1699 46.876L33.2676 47.1045C32.9608 47.2357 32.651 47.3611 32.3379 47.4795L32.25 47.2471C31.9414 47.3638 31.6299 47.4743 31.3154 47.5781L31.3926 47.8135C31.076 47.918 30.7567 48.0161 30.4346 48.1074L30.3672 47.8691C30.0502 47.959 29.7304 48.0416 29.4082 48.1182L29.4648 48.3613C29.1406 48.4384 28.8135 48.5068 28.4844 48.5703L28.4385 48.3271C28.1148 48.3896 27.7882 48.4452 27.46 48.4941L27.4961 48.7402C27.1665 48.7893 26.8348 48.8319 26.501 48.8672L26.4756 48.6201C26.1484 48.6547 25.8193 48.6822 25.4883 48.7031L25.5029 48.9502C25.171 48.9711 24.8374 48.9861 24.502 48.9932L24.498 48.7451C24.3326 48.7486 24.1663 48.75 24 48.75C23.8337 48.75 23.6674 48.7486 23.502 48.7451L23.4971 48.9932C23.1616 48.9861 22.828 48.9711 22.4961 48.9502L22.5117 48.7031C22.1807 48.6822 21.8516 48.6547 21.5244 48.6201L21.498 48.8672C21.1642 48.8319 20.8325 48.7894 20.5029 48.7402L20.54 48.4941C20.2118 48.4452 19.8852 48.3896 19.5615 48.3271L19.5146 48.5703C19.1855 48.5068 18.8584 48.4384 18.5342 48.3613L18.5918 48.1182C18.2696 48.0416 17.9498 47.959 17.6328 47.8691L17.5645 48.1074C17.2423 48.0161 16.923 47.918 16.6064 47.8135L16.6846 47.5781C16.3701 47.4743 16.0586 47.3638 15.75 47.2471L15.6611 47.4795C15.348 47.361 15.0382 47.2357 14.7314 47.1045L14.8301 46.876C14.5257 46.7458 14.2245 46.6094 13.9268 46.4668L13.8184 46.6904C13.5167 46.5459 13.2185 46.3951 12.9238 46.2383L13.041 46.0195C12.7489 45.8641 12.4606 45.7024 12.1758 45.5352L12.0488 45.75C11.7604 45.5806 11.476 45.4048 11.1953 45.2236L11.3311 45.0156C11.0531 44.8363 10.7788 44.6506 10.5088 44.46L10.3652 44.6621C10.0922 44.4693 9.8223 44.2723 9.55762 44.0684L9.70996 43.8711C9.44819 43.6694 9.19069 43.4623 8.9375 43.25L8.77637 43.4404C8.52025 43.2257 8.26955 43.0045 8.02246 42.7793L8.19043 42.5967C7.94606 42.3739 7.70584 42.1458 7.4707 41.9131L7.2959 42.0889C7.05842 41.8538 6.82451 41.615 6.59668 41.3701L6.78027 41.2012C6.55508 40.9592 6.3345 40.7123 6.11914 40.4609L5.92969 40.623C5.71234 40.3694 5.50105 40.1104 5.29395 39.8477L5.48926 39.6943C5.28463 39.4348 5.08484 39.1705 4.89062 38.9023L4.68848 39.0479C4.49276 38.7776 4.30211 38.5032 4.11719 38.2246L4.3252 38.0869C4.14268 37.8119 3.96554 37.5329 3.79395 37.25L3.58105 37.3779C3.40799 37.0926 3.23988 36.8037 3.07812 36.5107L3.2959 36.3906C3.13639 36.1017 2.98305 35.8088 2.83496 35.5127L2.61133 35.624C2.46204 35.3255 2.31913 35.0232 2.18164 34.7178L2.40918 34.6162C2.27365 34.3152 2.14416 34.0107 2.02051 33.7031L1.78906 33.7959C1.6645 33.4861 1.54495 33.1735 1.43262 32.8574L1.66895 32.7754C1.55847 32.4646 1.45371 32.1505 1.35547 31.834L1.11719 31.9062C1.01845 31.588 0.926052 31.267 0.839844 30.9434L1.08105 30.8809C0.996187 30.5623 0.917052 30.2407 0.844727 29.917L0.600586 29.9707C0.527979 29.6457 0.463128 29.318 0.40332 28.9883L0.647461 28.9443C0.588601 28.6198 0.536303 28.2928 0.490234 27.9639L0.244141 27.998C0.19792 27.668 0.156247 27.3361 0.123047 27.002L0.37207 26.9775C0.339524 26.65 0.313598 26.3205 0.293945 25.9893L0.0458984 26.0029C0.0262196 25.671 0.0124622 25.3373 0.00585938 25.002L0.254883 24.998C0.251616 24.8324 0.25 24.6664 0.25 24.5C0.25 24.3336 0.251616 24.1676 0.254883 24.002L0.00585938 23.9971C0.0124751 23.6617 0.0262068 23.328 0.0458984 22.9961L0.293945 23.0107C0.313598 22.6795 0.339524 22.35 0.37207 22.0225L0.124023 21.998L0.123047 21.9971C0.156259 21.6629 0.197907 21.331 0.244141 21.001L0.490234 21.0361C0.536303 20.7072 0.588602 20.3802 0.647461 20.0557L0.40332 20.0107C0.463141 19.681 0.527967 19.3533 0.600586 19.0283L0.844727 19.083C0.917051 18.7593 0.996187 18.4377 1.08105 18.1191L0.839844 18.0557C0.926063 17.732 1.01844 17.411 1.11719 17.0928L1.35547 17.166C1.45371 16.8495 1.55847 16.5354 1.66895 16.2246L1.43262 16.1416C1.54496 15.8256 1.66449 15.5129 1.78906 15.2031L2.02051 15.2969C2.14416 14.9893 2.27365 14.6848 2.40918 14.3838L2.18164 14.2812C2.31914 13.9758 2.46204 13.6735 2.61133 13.375L2.83496 13.4873C2.98305 13.1912 3.13639 12.8983 3.2959 12.6094L3.07812 12.4883C3.23989 12.1953 3.40798 11.9064 3.58105 11.6211L3.79395 11.75C3.96554 11.4671 4.14268 11.1881 4.3252 10.9131L4.11719 10.7744C4.21911 10.6209 4.32252 10.4684 4.42773 10.3174L4.68848 9.95117L4.89062 10.0977C5.08484 9.82947 5.28463 9.56523 5.48926 9.30566L5.29395 9.15137C5.50105 8.88866 5.71234 8.62961 5.92969 8.37598L6.11914 8.53906C6.3345 8.28774 6.55507 8.04082 6.78027 7.79883L6.59668 7.62891C6.82451 7.38407 7.05842 7.14522 7.2959 6.91016L7.4707 7.08691C7.70584 6.85417 7.94606 6.62605 8.19043 6.40332L8.02246 6.21973C8.26955 5.99451 8.52025 5.77336 8.77637 5.55859L8.9375 5.75C9.19069 5.5377 9.44819 5.33056 9.70996 5.12891L9.55762 4.93066C9.8223 4.72678 10.0922 4.5297 10.3652 4.33691L10.5088 4.54004C10.7788 4.34939 11.0531 4.16373 11.3311 3.98438L11.1953 3.77539C11.3146 3.69844 11.434 3.62172 11.5547 3.54688L12.0488 3.24902L12.1758 3.46484C12.4606 3.29757 12.7489 3.13586 13.041 2.98047L12.9238 2.76074C13.2185 2.60396 13.5167 2.45309 13.8184 2.30859L13.9268 2.5332C14.2245 2.39059 14.5257 2.25422 14.8301 2.12402L14.7314 1.89453C15.0382 1.7633 15.348 1.63797 15.6611 1.51953L15.75 1.75293C16.0586 1.63621 16.3701 1.52572 16.6846 1.42188L16.6064 1.18555C16.923 1.08102 17.2423 0.982915 17.5645 0.891602L17.6328 1.13086C17.9498 1.04102 18.2696 0.958441 18.5918 0.881836L18.5342 0.637695C18.8584 0.56061 19.1855 0.492229 19.5146 0.428711L19.5615 0.672852C19.8852 0.610376 20.2118 0.554773 20.54 0.505859L20.5029 0.258789C20.8325 0.209669 21.1642 0.167133 21.498 0.131836L21.5244 0.379883C21.8516 0.34529 22.1807 0.317767 22.5117 0.296875L22.4961 0.0488281C22.828 0.0278779 23.1616 0.0128984 23.4971 0.00585938L23.502 0.254883C23.6674 0.25141 23.8337 0.25 24 0.25C24.1663 0.25 24.3326 0.25141 24.498 0.254883L24.502 0.00585938C24.8374 0.0128848 25.171 0.0278915 25.5029 0.0488281L25.4883 0.296875C25.8193 0.317767 26.1484 0.34529 26.4756 0.379883L26.501 0.131836C26.8348 0.16712 27.1665 0.209682 27.4961 0.258789L27.46 0.505859C27.7882 0.554773 28.1148 0.610376 28.4385 0.672852L28.4844 0.428711C28.8135 0.492216 29.1406 0.560622 29.4648 0.637695L29.4082 0.881836C29.7304 0.958441 30.0502 1.04102 30.3672 1.13086L30.4346 0.891602C30.7567 0.982903 31.076 1.08103 31.3926 1.18555L31.3154 1.42188C31.6299 1.52572 31.9414 1.63621 32.25 1.75293L32.3379 1.51953C32.651 1.63796 32.9608 1.76331 33.2676 1.89453L33.1699 2.12402C33.4743 2.25422 33.7755 2.39059 34.0732 2.5332L34.1807 2.30859C34.4824 2.45309 34.7805 2.60397 35.0752 2.76074L34.959 2.98047C35.2511 3.13586 35.5394 3.29757 35.8242 3.46484L35.9502 3.24902C36.2386 3.41842 36.523 3.59427 36.8037 3.77539L36.6689 3.98438C36.9469 4.16373 37.2212 4.34939 37.4912 4.54004L37.6338 4.33691C37.9069 4.52969 38.1767 4.72679 38.4414 4.93066L38.29 5.12891C38.5518 5.33056 38.8093 5.5377 39.0625 5.75L39.2227 5.55859C39.4788 5.77335 39.7295 5.99451 39.9766 6.21973L39.8096 6.40332C40.0539 6.62605 40.2942 6.85417 40.5293 7.08691L40.7031 6.91016C40.9406 7.14522 41.1745 7.38407 41.4023 7.62891L41.2197 7.79883C41.4449 8.04082 41.6655 8.28774 41.8809 8.53906L42.0693 8.37598C42.2867 8.62961 42.498 8.88866 42.7051 9.15137L42.5107 9.30566C42.7154 9.56523 42.9152 9.82947 43.1094 10.0977L43.3105 9.95117Z" stroke="#B2E3CC" stroke-width="0.5" stroke-dasharray="1 1" />
                <ellipse cx="24" cy="24.5" rx="19" ry="19.5" fill="url(#paint0_linear_17825_1911)" />
                <path d="M24.412 14.0057C24.3434 14.0126 24.2761 14.0287 24.2119 14.0534C21.7583 15.03 19.1639 15.9978 14.7452 16.1373C14.5456 16.1435 14.3563 16.2267 14.2174 16.3692C14.0785 16.5118 14.0009 16.7024 14.001 16.9009C13.9988 17.7503 14.0009 22.5376 14.001 23.3195C14.0011 27.656 15.3366 30.7565 17.3139 32.8958C19.2912 35.035 21.8612 36.203 24.2919 36.8806C24.428 36.9186 24.572 36.9186 24.7081 36.8806C27.1388 36.203 29.7088 35.035 31.6861 32.8958C33.6633 30.7565 34.9989 27.656 34.999 23.3195C34.9991 22.5376 35.0012 17.7503 34.999 16.9009C34.9991 16.7024 34.9215 16.5118 34.7826 16.3692C34.6437 16.2267 34.4544 16.1435 34.2548 16.1373C29.8361 15.9978 27.2417 15.03 24.7881 14.0534C24.6687 14.0065 24.5394 13.9901 24.412 14.0057ZM24.5 15.5885C26.7709 16.4846 29.4569 17.3747 33.4626 17.6008C33.4637 19.0236 33.4626 22.6197 33.4626 23.3195C33.4625 27.3514 32.2728 30.0062 30.5578 31.8618C28.8957 33.66 26.6908 34.7009 24.5 35.3375C22.3092 34.7009 20.1043 33.66 18.4422 31.8618C16.7271 30.0062 15.5375 27.3514 15.5374 23.3195C15.5374 22.6197 15.5363 19.0236 15.5374 17.6008C19.5431 17.3747 22.2291 16.4846 24.5 15.5885ZM29.3254 21.1243C29.1264 21.1363 28.9399 21.2247 28.8052 21.3708L22.7315 27.869L20.2188 24.958C20.1532 24.8812 20.0731 24.8181 19.983 24.7722C19.8928 24.7263 19.7945 24.6986 19.6935 24.6906C19.5926 24.6826 19.491 24.6945 19.3947 24.7256C19.2984 24.7567 19.2092 24.8065 19.1323 24.8719C19.0554 24.9374 18.9922 25.0173 18.9464 25.1071C18.9007 25.1969 18.8732 25.2948 18.8656 25.3951C18.858 25.4955 18.8704 25.5964 18.9021 25.692C18.9338 25.7876 18.9842 25.876 19.0504 25.9522L22.1233 29.5154C22.1937 29.5969 22.2805 29.6627 22.3782 29.7085C22.4758 29.7544 22.5821 29.7792 22.6901 29.7815C22.7981 29.7837 22.9053 29.7634 23.0048 29.7216C23.1044 29.6799 23.1939 29.6178 23.2676 29.5394L29.9256 22.4129C30.0317 22.3019 30.1017 22.1619 30.1268 22.0109C30.1518 21.8598 30.1306 21.7048 30.0659 21.5659C30.0012 21.4271 29.896 21.3107 29.764 21.232C29.6319 21.1532 29.4791 21.1157 29.3254 21.1243Z" fill="white" stroke="white" stroke-width="0.5" />
                <circle cx="38.5" cy="5.5" r="2.5" fill="#0D9F5A" />
                <circle cx="6.5" cy="40.5" r="2.5" fill="#0D9F5A" />
                <defs>
                  <linearGradient id="paint0_linear_17825_1911" x1="24" y1="5" x2="32" y2="44" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#29B465" />
                    <stop offset="1" stop-color="#007B45" />
                  </linearGradient>
                </defs>
              </svg>

            </span>
            <div className={styles.guaranteeText}>
              <p className={styles.guaranteePercent}>
                100<span className={styles.percentSign}>%</span>
              </p>
              <p className={styles.guaranteeLabel}>
                Money-Back<br />
                <span className={styles.guaranteeUnderline}>Guarantee</span>
              </p>
            </div>
          </div>
        </div>

        <p className={styles.fineNote}>
          After trial, pick{' '}
          <span className={styles.link} onClick={handleOpenPlans}>suitable plan </span>
           or keep Starter (just $119/mo).
        </p>
        <p className={styles.reminderNote}>
          We'll remind you 2 days before you're charged.{' '}
          <span className={styles.noSurprise}>No Surprise</span>
        </p>

        {/* Top plan hero */}
        <div className={styles.hero} onClick={handleOpenPlans}>
          <div className={styles.heroText}>
            <h3 className={styles.heroTitle}>
              <span className={styles.heroTitleLight}>Experience full features of our</span><br />
              <span className={styles.heroTier}>SCALER TIER</span> worth <span className={styles.heroPrice}>$299</span>
            </h3>
            <p className={styles.heroSub}>Real calls | Real customers | Full functionality</p>
          </div>
          <div className={styles.heroBadge}>
            <img src="/svg/21-day_icon.svg" alt="Plan Icon" />
          </div>
        </div>

        {/* Benefits */}
        <ul className={styles.benefits}>
          {BENEFITS.map((b) => (
            <li key={b}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#22C55E" />
                <path d="M8 12l2.5 2.5L16 9" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {b}
            </li>
          ))}
        </ul>

        {/* Cost note */}
        <div className={styles.costNote}>
          <span className={styles.costIcon}>
            <svg width="40" height="41" viewBox="0 0 40 41" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M33.1396 5.02539C33.3864 5.24514 33.6284 5.47046 33.8643 5.70215L33.6904 5.88086C33.9232 6.10951 34.1505 6.34401 34.3721 6.58398L34.5547 6.41504C34.7788 6.65774 34.9966 6.90647 35.209 7.16016L35.0195 7.32031C35.2289 7.57044 35.4325 7.82642 35.6299 8.08691L35.8271 7.93555C36.0267 8.19882 36.2203 8.46696 36.4072 8.74023L36.2021 8.88184C36.3863 9.15103 36.5649 9.42455 36.7363 9.70312L36.9473 9.57227C37.1203 9.8535 37.2865 10.1395 37.4463 10.4297L37.2285 10.5498C37.3857 10.8354 37.5369 11.1259 37.6807 11.4199L37.9033 11.3105C38.0483 11.607 38.1863 11.9075 38.3174 12.2119L38.0898 12.3105C38.2186 12.6098 38.3403 12.9131 38.4551 13.2197L38.6875 13.1318C38.8032 13.4408 38.9115 13.7534 39.0127 14.0693L38.7764 14.1455C38.8756 14.4555 38.968 14.7686 39.0527 15.085L39.292 15.0205C39.3772 15.3388 39.4551 15.6601 39.5254 15.9844L39.2832 16.0381C39.3521 16.356 39.4137 16.6767 39.4678 17L39.7129 16.959C39.7671 17.2836 39.8135 17.6108 39.8525 17.9404L39.6064 17.9707C39.6447 18.2935 39.6751 18.619 39.6982 18.9463L39.9463 18.9287C39.9694 19.2567 39.9854 19.5868 39.9932 19.9189L39.7441 19.9248C39.748 20.0882 39.75 20.2526 39.75 20.417C39.75 20.5812 39.748 20.7449 39.7441 20.9082L39.9932 20.9131C39.9854 21.2455 39.9695 21.576 39.9463 21.9043L39.6982 21.8877C39.6751 22.215 39.6447 22.5405 39.6064 22.8633L39.8525 22.8916C39.8135 23.2212 39.7671 23.5484 39.7129 23.873L39.4678 23.833C39.4137 24.1565 39.3522 24.4779 39.2832 24.7959L39.5254 24.8477C39.4551 25.172 39.3772 25.4932 39.292 25.8115L39.0527 25.748C38.968 26.0646 38.8756 26.3784 38.7764 26.6885L39.0127 26.7637C38.9116 27.0794 38.803 27.3915 38.6875 27.7002L38.4551 27.6143C38.3403 27.9208 38.2186 28.2243 38.0898 28.5234L38.3174 28.6211C38.1863 28.9255 38.0483 29.226 37.9033 29.5225L37.6807 29.4131C37.5369 29.7071 37.3857 29.9976 37.2285 30.2832L37.4463 30.4023C37.2865 30.6926 37.1203 30.9785 36.9473 31.2598L36.7363 31.1299C36.5649 31.4085 36.3863 31.6829 36.2021 31.9521L36.4072 32.0918C36.2203 32.3651 36.0267 32.6332 35.8271 32.8965L35.6299 32.7471C35.4324 33.0077 35.229 33.2634 35.0195 33.5137L35.21 33.6729C34.9975 33.9267 34.779 34.1751 34.5547 34.418L34.3721 34.249C34.1505 34.489 33.9232 34.7235 33.6904 34.9521L33.8643 35.1299C33.6282 35.3617 33.3866 35.5877 33.1396 35.8076L32.9746 35.6221C32.7307 35.8393 32.4808 36.05 32.2266 36.2549L32.3828 36.4492C32.1255 36.6566 31.863 36.8573 31.5957 37.0518L31.4492 36.8506C31.1854 37.0426 30.9168 37.2284 30.6436 37.4072L30.7803 37.6162C30.5035 37.7973 30.2214 37.9703 29.9355 38.1377L29.8105 37.9229C29.5291 38.0877 29.2431 38.2456 28.9531 38.3965L29.0674 38.6182C28.7745 38.7705 28.4777 38.9159 28.1768 39.0537L28.0732 38.8271C27.7769 38.9628 27.4767 39.0909 27.1729 39.2119L27.2646 39.4434C26.958 39.5655 26.6478 39.6802 26.334 39.7871L26.2539 39.5518C25.9458 39.6567 25.634 39.7541 25.3193 39.8438L25.3867 40.082C25.0697 40.1723 24.7499 40.2555 24.4268 40.3301L24.3711 40.0879C24.0543 40.161 23.7345 40.2259 23.4121 40.2832L23.4551 40.5283C23.1305 40.586 22.8033 40.6352 22.4736 40.6768L22.4434 40.4307C22.1212 40.4713 21.7966 40.5038 21.4697 40.5283L21.4873 40.7764C21.1593 40.801 20.8293 40.8189 20.4971 40.8271L20.4912 40.5771C20.328 40.5812 20.1642 40.583 20 40.583C19.8358 40.583 19.672 40.5812 19.5088 40.5771L19.5029 40.8271H19.4834C19.1575 40.8187 18.8336 40.8005 18.5117 40.7764L18.5303 40.5283C18.2034 40.5038 17.8788 40.4713 17.5566 40.4307L17.5254 40.6768C17.1957 40.6352 16.8685 40.586 16.5439 40.5283L16.5879 40.2832C16.2655 40.2259 15.9457 40.161 15.6289 40.0879L15.5723 40.3301C15.2492 40.2555 14.9293 40.1723 14.6123 40.082L14.6807 39.8438C14.366 39.7541 14.0542 39.6567 13.7461 39.5518L13.665 39.7871C13.3513 39.6802 13.041 39.5655 12.7344 39.4434L12.8271 39.2119C12.5233 39.0909 12.2231 38.9628 11.9268 38.8271L11.8223 39.0537C11.5213 38.9159 11.2245 38.7705 10.9316 38.6182L11.0469 38.3965C10.7569 38.2457 10.4709 38.0877 10.1895 37.9229L10.0635 38.1377C9.7779 37.9705 9.49622 37.7972 9.21973 37.6162L9.35645 37.4072C9.0832 37.2284 8.81462 37.0426 8.55078 36.8506L8.40332 37.0518C8.13607 36.8573 7.87347 36.6566 7.61621 36.4492L7.77344 36.2549C7.51919 36.05 7.26932 35.8393 7.02539 35.6221L6.85938 35.8076C6.61242 35.5877 6.37081 35.3617 6.13477 35.1299L6.30957 34.9521C6.0768 34.7235 5.84953 34.489 5.62793 34.249L5.44434 34.418C5.22008 34.1751 5.00156 33.9267 4.78906 33.6729L4.98047 33.5137C4.77099 33.2634 4.56759 33.0077 4.37012 32.7471L4.17188 32.8965C3.97238 32.6332 3.77875 32.3651 3.5918 32.0918L3.79785 31.9521C3.61366 31.6829 3.4351 31.4085 3.26367 31.1299L3.05176 31.2598C2.87873 30.9785 2.71248 30.6926 2.55273 30.4023L2.77148 30.2832C2.61426 29.9976 2.46312 29.7071 2.31934 29.4131L2.0957 29.5225C1.95075 29.226 1.81267 28.9255 1.68164 28.6211L1.91016 28.5234C1.78138 28.2243 1.65967 27.9208 1.54492 27.6143L1.31152 27.7002C1.19601 27.3915 1.08738 27.0793 0.986328 26.7637L1.22363 26.6885C1.12435 26.3784 1.03202 26.0646 0.947266 25.748L0.707031 25.8115C0.621824 25.4932 0.543932 25.1719 0.473633 24.8477L0.716797 24.7959C0.647844 24.4779 0.586283 24.1565 0.532227 23.833L0.286133 23.873C0.231911 23.5484 0.185506 23.2212 0.146484 22.8916L0.393555 22.8633C0.35533 22.5405 0.324865 22.215 0.301758 21.8877L0.0527344 21.9043C0.029574 21.576 0.0136303 21.2455 0.00585938 20.9131L0.255859 20.9082L0.25 20.417C0.25 20.2526 0.252032 20.0882 0.255859 19.9248L0.00585938 19.9189C0.013643 19.5868 0.0295916 19.2567 0.0527344 18.9287L0.301758 18.9463C0.324857 18.619 0.35534 18.2935 0.393555 17.9707L0.146484 17.9404C0.185526 17.6108 0.231891 17.2836 0.286133 16.959L0.532227 17C0.58626 16.6767 0.647881 16.356 0.716797 16.0381L0.473633 15.9844C0.54395 15.6601 0.621806 15.3388 0.707031 15.0205L0.947266 15.085C1.03198 14.7686 1.12441 14.4555 1.22363 14.1455L0.986328 14.0693C1.08748 13.7534 1.19588 13.4408 1.31152 13.1318L1.54492 13.2197C1.65968 12.9131 1.78136 12.6098 1.91016 12.3105L1.68164 12.2119C1.81267 11.9075 1.95075 11.607 2.0957 11.3105L2.31934 11.4199C2.46312 11.1259 2.61426 10.8354 2.77148 10.5498L2.55273 10.4297C2.71249 10.1395 2.87872 9.85349 3.05176 9.57227L3.26367 9.70312C3.43507 9.42455 3.6137 9.15103 3.79785 8.88184L3.5918 8.74023C3.77875 8.46697 3.97237 8.19881 4.17188 7.93555L4.37012 8.08691C4.56751 7.82642 4.77109 7.57044 4.98047 7.32031L4.79004 7.16016C5.00241 6.90648 5.22022 6.65774 5.44434 6.41504L5.62793 6.58398C5.84954 6.34401 6.07679 6.10951 6.30957 5.88086L6.13477 5.70215C6.27212 5.56724 6.41165 5.43461 6.55273 5.30371L6.85938 5.02539L7.02539 5.21191C7.26934 4.99469 7.51917 4.78307 7.77344 4.57812L7.61621 4.38379C7.87361 4.17632 8.13591 3.97487 8.40332 3.78027L8.55078 3.98242C8.81464 3.79041 9.08318 3.60464 9.35645 3.42578L9.21973 3.2168C9.49039 3.03965 9.76556 2.86906 10.0449 2.70508L10.0635 2.69434L10.1895 2.91016C10.471 2.74532 10.7569 2.58738 11.0469 2.43652L10.9316 2.21484L10.9307 2.21387C11.2236 2.06146 11.5212 1.91717 11.8223 1.7793L11.9268 2.00586C12.223 1.87019 12.5234 1.74206 12.8271 1.62109L12.7344 1.38867C13.041 1.26657 13.3513 1.1518 13.665 1.04492L13.7461 1.28223C14.0543 1.17726 14.3659 1.0799 14.6807 0.990234L14.6123 0.75C14.9293 0.659707 15.2492 0.576493 15.5723 0.501953L15.6289 0.745117C15.9457 0.672035 16.2655 0.60713 16.5879 0.549805L16.5439 0.304688C16.8685 0.246969 17.1957 0.196812 17.5254 0.155273L17.5566 0.402344C17.8788 0.361759 18.2035 0.329232 18.5303 0.304688L18.5117 0.0556641C18.84 0.0310136 19.1704 0.0141384 19.5029 0.00585938L19.5088 0.255859C19.672 0.251791 19.8358 0.25 20 0.25C20.1642 0.25 20.328 0.251791 20.4912 0.255859L20.4961 0.00585938C20.8286 0.0141221 21.159 0.0310298 21.4873 0.0556641L21.4697 0.304688C21.7965 0.329232 22.1212 0.361759 22.4434 0.402344L22.4736 0.155273C22.8034 0.196796 23.1305 0.246985 23.4551 0.304688L23.4121 0.549805C23.7345 0.60713 24.0543 0.672035 24.3711 0.745117L24.4268 0.501953C24.7499 0.576478 25.0698 0.659721 25.3867 0.75L25.3193 0.990234C25.6341 1.0799 25.9457 1.17726 26.2539 1.28223L26.334 1.04492C26.6478 1.15179 26.958 1.26658 27.2646 1.38867L27.1729 1.62109C27.4766 1.74206 27.777 1.87019 28.0732 2.00586L28.1768 1.7793C28.4778 1.91716 28.7754 2.06147 29.0684 2.21387V2.21484L28.9531 2.43652C29.2431 2.58738 29.529 2.74532 29.8105 2.91016L29.9355 2.69434C30.2215 2.86177 30.5034 3.03558 30.7803 3.2168L30.6436 3.42578C30.9168 3.60464 31.1854 3.79041 31.4492 3.98242L31.5957 3.78027C31.8631 3.97487 32.1254 4.17632 32.3828 4.38379L32.2266 4.57812C32.4808 4.78307 32.7307 4.99469 32.9746 5.21191L33.1396 5.02539Z" stroke="#CCB4FF" stroke-width="0.5" stroke-dasharray="1 1" />
              <ellipse cx="20.0003" cy="20.418" rx="15.8333" ry="16.25" fill="url(#paint0_linear_17825_1865)" />
              <path d="M20.3436 11.6727C20.2865 11.6785 20.2304 11.6919 20.1769 11.7125C18.1322 12.5263 15.9702 13.3328 12.288 13.4491C12.1217 13.4542 11.9639 13.5236 11.8482 13.6423C11.7324 13.7611 11.6677 13.92 11.6678 14.0853C11.666 14.7932 11.6678 18.7827 11.6678 19.4342C11.6679 23.048 12.7809 25.6317 14.4286 27.4144C16.0763 29.1972 18.218 30.1704 20.2436 30.7351C20.357 30.7668 20.477 30.7668 20.5904 30.7351C22.616 30.1704 24.7577 29.1972 26.4054 27.4144C28.0531 25.6317 29.1661 23.048 29.1662 19.4342C29.1662 18.7827 29.168 14.7932 29.1662 14.0853C29.1663 13.92 29.1016 13.7611 28.9858 13.6423C28.87 13.5236 28.7123 13.4542 28.546 13.4491C24.8638 13.3328 22.7018 12.5263 20.6571 11.7125C20.5576 11.6734 20.4498 11.6597 20.3436 11.6727ZM20.417 12.9917C22.3094 13.7385 24.5478 14.4802 27.8858 14.6686C27.8868 15.8543 27.8858 18.8511 27.8858 19.4342C27.8857 22.7941 26.8944 25.0064 25.4651 26.5528C24.0801 28.0513 22.2427 28.9187 20.417 29.4492C18.5913 28.9187 16.7539 28.0513 15.3689 26.5528C13.9396 25.0064 12.9482 22.7941 12.9482 19.4342C12.9481 18.8511 12.9472 15.8543 12.9482 14.6686C16.2862 14.4802 18.5246 13.7385 20.417 12.9917ZM24.4382 17.6049C24.2723 17.6149 24.1169 17.6885 24.0047 17.8103L18.9432 23.2255L16.8493 20.7996C16.7947 20.7356 16.7279 20.683 16.6528 20.6448C16.5777 20.6066 16.4957 20.5835 16.4116 20.5768C16.3275 20.5701 16.2428 20.5801 16.1626 20.606C16.0823 20.6319 16.008 20.6734 15.9439 20.7279C15.8798 20.7825 15.8272 20.849 15.789 20.9239C15.7509 20.9987 15.728 21.0803 15.7216 21.1639C15.7153 21.2476 15.7256 21.3316 15.7521 21.4113C15.7785 21.4909 15.8205 21.5646 15.8757 21.6281L18.4364 24.5975C18.495 24.6654 18.5674 24.7202 18.6488 24.7584C18.7302 24.7966 18.8188 24.8173 18.9087 24.8192C18.9987 24.8211 19.0881 24.8041 19.171 24.7693C19.254 24.7346 19.3286 24.6828 19.39 24.6174L24.9383 18.6787C25.0267 18.5862 25.0851 18.4695 25.106 18.3437C25.1268 18.2178 25.1091 18.0887 25.0552 17.9729C25.0013 17.8572 24.9137 17.7602 24.8036 17.6946C24.6936 17.629 24.5663 17.5977 24.4382 17.6049Z" fill="white" stroke="white" stroke-width="0.5" />
              <circle cx="32.0833" cy="4.58333" r="2.08333" fill="#6524EB" />
              <circle cx="5.41634" cy="33.7513" r="2.08333" fill="#6524EB" />
              <defs>
                <linearGradient id="paint0_linear_17825_1865" x1="20.0003" y1="4.16797" x2="26.667" y2="36.668" gradientUnits="userSpaceOnUse">
                  <stop stop-color="#9B6BFF" />
                  <stop offset="1" stop-color="#5308EE" />
                </linearGradient>
              </defs>
            </svg>

          </span>
          <p className={styles.costText}>
            <strong>Not happy for any reason in 21 days?</strong> <br />Get a full refund with one tap. No emails. No questions
          </p>
        </div>
      </div>

      {/* Full-screen click blocker overlay while checkout navigation is in progress */}
      {(trialLoading || paygLoading || checkoutLoading) && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999999,
            cursor: 'wait',
            background: 'transparent',
            pointerEvents: 'all',
          }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        />,
        document.body
      )}

      {/* Fixed footer — stays pinned; the modal slides up from behind it and the
          CheckoutBar content updates once a plan is being chosen. */}
      {forcedOffer !== 'plans' && createPortal(<div className={styles.footer}>
        {!planOpen && cameFromUpgrade && (
          <button
            type="button"
            className={styles.checkPlanBtn}
            onClick={handlePaygCheckout}
            disabled={paygLoading || trialLoading || checkoutLoading}
          >
            <span className={styles.checkPlanText}>
              {paygLoading ? 'Please wait…' : 'Not sure? Start with $5/mo plan ›'}
            </span>
          </button>
        )}

        {/* tagPill only in the trial (closed) state, not the Continue state. */}
        {!planOpen && <TrustRow />}

        {planOpen ? (
          // Continue (plan checkout) — gated like the trial button: hidden until
          // the user agrees (confirm_go_live) or changes the plan.
          revealTrialCTA ? (
            <CheckoutBar
              label={`${yearly ? 'yearly' : 'monthly'} subscription`}
              amount={`$${priceOf(selectedPlan, yearly)}`}
              buttonText={checkoutLoading ? 'Please wait…' : 'Continue'}
              onClick={handlePlanCheckout}
            />
          ) : null
        ) : revealTrialCTA ? (
          <>
            <AnimatedButton
              label={trialLoading || paygLoading ? 'Please wait…' : 'Start My 21-Day Trial  — $20'}
              isLoading={trialLoading || paygLoading}
              disabled={trialLoading || paygLoading || checkoutLoading}
              onClick={handleStartTrial}
              positionType="relative"
              width="100%"
              bottom="0"
            />
            <p className={styles.secureLine}>
              <span className={styles.secureItem}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <rect x="5" y="11" width="14" height="9" rx="2" stroke="#6b7280" strokeWidth="1.7" />
                  <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="#6b7280" strokeWidth="1.7" />
                </svg>
                100% Secure.
              </span>
              <span className={styles.secureItem}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M5 13l4 4L19 7" stroke="#6b7280" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Cancel anytime.
              </span>
              <span className={styles.secureItem}>
                <strong className={styles.stripeWord}>stripe</strong> Checkout
              </span>
            </p>
          </>
        ) : null}
      </div>, document.body)}

      <ChoosePlanModal
        isOpen={planOpen}
        onClose={() => {
          injectContext(
            "[SYSTEM NOTE: The customer just closed/dismissed the plan selection popup. Speak directly to the customer in 1 short, warm sentence: ask why they closed the popup and if they didn't like the plans. Do NOT read this instruction out loud.]"
          );
          setPlanOpen(false);
        }}
        yearly={yearly}
        setYearly={setYearly}
        selected={selected}
        setSelected={(val) => {
          // Changing the plan counts as engagement → reveal the pay CTA.
          setSelected(val);
          setPlanChanged(true);
        }}
      />
    </div>
  )
}

export default TrialOffer
